const DEFAULT_THEME = {
  iconActive: '#111827',
  iconInactive: '#9ca3af',
  title: '#0a222b',
  text: '#0a222b',
  primary: '#088395',
  primaryContrast: '#ffffff',
  background: '#ffffff',
  surface: '#ffffff',
  border: '#e5e7eb',
  muted: '#6b7a80',
  chartPalette: ['#3e95cd', '#8e5ea2', '#3cba9f', '#e8c3b9', '#c45850', '#2f4b7c', '#f95d6a', '#ffa600'],
  fontTitleFamily: 'Roboto, sans-serif',
  fontTextFamily: 'Roboto, sans-serif',
  fontTitleSize: '20px',
  fontTextSize: '16px',
  fontMutedSize: '12px',
};

const sanitizeColor = (value, fallback) => {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
};


const sanitizeFontSize = (value, fallback) => {
  if (typeof value === 'number' && !Number.isNaN(value)) return `${value}px`;
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  if (/^\d+(\.\d+)?px$/i.test(trimmed)) return trimmed;
  if (/^\d+(\.\d+)?$/.test(trimmed)) return `${trimmed}px`;
  return fallback;
};

const sanitizeFontFamily = (value, fallback) => {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  return trimmed || fallback;
};

const sanitizePalette = (value, fallback) => {
  if (!Array.isArray(value) || value.length == 0) {
    return Array.isArray(fallback) ? [...fallback] : [];
  }
  const cleaned = value
    .map((item) => sanitizeColor(item, ''))
    .filter((item) => item);
  return cleaned.length ? cleaned : (Array.isArray(fallback) ? [...fallback] : []);
};


export const normalizeTheme = (theme = {}) => ({
  iconActive: sanitizeColor(theme.iconActive, DEFAULT_THEME.iconActive),
  iconInactive: sanitizeColor(theme.iconInactive, DEFAULT_THEME.iconInactive),
  title: sanitizeColor(theme.title, DEFAULT_THEME.title),
  text: sanitizeColor(theme.text, DEFAULT_THEME.text),
  primary: sanitizeColor(theme.primary, DEFAULT_THEME.primary),
  primaryContrast: sanitizeColor(theme.primaryContrast, DEFAULT_THEME.primaryContrast),
  background: sanitizeColor(theme.background, DEFAULT_THEME.background),
  surface: sanitizeColor(theme.surface, DEFAULT_THEME.surface),
  border: sanitizeColor(theme.border, DEFAULT_THEME.border),
  muted: sanitizeColor(theme.muted, DEFAULT_THEME.muted),
  chartPalette: sanitizePalette(theme.chartPalette, DEFAULT_THEME.chartPalette),
  fontTitleFamily: sanitizeFontFamily(theme.fontTitleFamily, DEFAULT_THEME.fontTitleFamily),
  fontTextFamily: sanitizeFontFamily(theme.fontTextFamily, DEFAULT_THEME.fontTextFamily),
  fontTitleSize: sanitizeFontSize(theme.fontTitleSize, DEFAULT_THEME.fontTitleSize),
  fontTextSize: sanitizeFontSize(theme.fontTextSize, DEFAULT_THEME.fontTextSize),
  fontMutedSize: sanitizeFontSize(theme.fontMutedSize, DEFAULT_THEME.fontMutedSize),
});

export const getDefaultTheme = () => {
  const normalized = normalizeTheme(DEFAULT_THEME);
  return {
    ...normalized,
    chartPalette: Array.isArray(normalized.chartPalette) ? [...normalized.chartPalette] : [],
  };
};

export const applyTheme = (theme) => {
  const normalized = normalizeTheme(theme);
  const root = document.documentElement;
  root.style.setProperty('--color-icon-active', normalized.iconActive);
  root.style.setProperty('--color-icon-inactive', normalized.iconInactive);
  root.style.setProperty('--color-title', normalized.title);
  root.style.setProperty('--color-text', normalized.text);
  root.style.setProperty('--color-primary', normalized.primary);
  root.style.setProperty('--color-primary-contrast', normalized.primaryContrast);
  root.style.setProperty('--color-bg', normalized.background);
  root.style.setProperty('--color-surface', normalized.surface);
  root.style.setProperty('--color-border', normalized.border);
  root.style.setProperty('--color-muted', normalized.muted);
  normalized.chartPalette.forEach((color, index) => {
    root.style.setProperty(`--chart-color-${index + 1}`, color);
  });
  root.style.setProperty('--font-title-family', normalized.fontTitleFamily);
  root.style.setProperty('--font-text-family', normalized.fontTextFamily);
  root.style.setProperty('--font-title-size', normalized.fontTitleSize);
  root.style.setProperty('--font-text-size', normalized.fontTextSize);
  root.style.setProperty('--font-muted-size', normalized.fontMutedSize);
  return normalized;
};

export const getChartPalette = (theme) => normalizeTheme(theme).chartPalette;
